import { useRef } from 'react';

import { DownloadIcon, PlusIcon, Trash2Icon, UploadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { AnimalRow, AnimalTypeLabel } from '@/services/animal-management';
import { useToast } from '@/services/toast';

import { isAddFieldRequired } from './add-animal-schema';
import {
  BULK_TEMPLATE_HEADERS,
  type BulkAddRow,
  bulkRowColumns,
  type BulkRowField,
  bulkTemplateCsv,
  emptyBulkRow,
  isBlankRow,
  parseBulkRows,
} from './bulk-add-rows';
import { PossibleDuplicatesPanel } from './PossibleDuplicatesPanel';

const LABEL_KEYS: Record<BulkRowField, string> = {
  nameTag: 'animalManagement.columns.nameTag',
  name: 'animalManagement.columns.name',
  tag: 'animalManagement.columns.tag',
  rfidTag: 'animalManagement.columns.rfidTag',
  registrationNumber: 'animalManagement.registrationNumber',
  birthDate: 'animalManagement.columns.birthDate',
};

type BulkAddRowsProps = {
  animalType: AnimalTypeLabel;
  rows: BulkAddRow[];
  onRowsChange: (rows: BulkAddRow[]) => void;
  onOpenExisting?: (animal: AnimalRow) => void;
  disabled?: boolean;
};

export function BulkAddRows({
  animalType,
  rows,
  onRowsChange,
  onOpenExisting,
  disabled,
}: BulkAddRowsProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const columns = bulkRowColumns(animalType);
  const label = (field: BulkRowField) =>
    t(LABEL_KEYS[field], BULK_TEMPLATE_HEADERS[field]);

  const updateRow = (key: string, update: Partial<BulkAddRow>) =>
    onRowsChange(
      rows.map((row) => (row.key === key ? { ...row, ...update } : row)),
    );

  const setValue = (row: BulkAddRow, field: BulkRowField, value: string) =>
    // Changing an identifying value makes the old duplicate check stale.
    updateRow(row.key, {
      values: { ...row.values, [field]: value },
      duplicates: null,
      reason: '',
      error: null,
    });

  const removeRow = (key: string) => {
    const next = rows.filter((row) => row.key !== key);
    onRowsChange(next.length ? next : [emptyBulkRow()]);
  };

  const importFile = async (file: File) => {
    const { rows: imported, unknownHeaders } = parseBulkRows(await file.text());
    if (!imported.length) {
      toast.show({
        severity: 'error',
        summary: t('common.error', 'Error'),
        detail: t(
          'animalManagement.bulkAdd.importEmpty',
          'No animals found. The first row must be column headers, e.g. {headers}.',
          { headers: bulkTemplateCsv(animalType).trim() },
        ),
        life: 8000,
      });
      return;
    }
    onRowsChange([...rows.filter((row) => !isBlankRow(row)), ...imported]);
    toast.show({
      severity: unknownHeaders.length ? 'warn' : 'success',
      summary: t(
        'animalManagement.bulkAdd.imported',
        '{count} row(s) imported',
        {
          count: imported.length,
        },
      ),
      detail: unknownHeaders.length
        ? t(
            'animalManagement.bulkAdd.ignoredColumns',
            'Ignored columns: {columns}',
            { columns: unknownHeaders.join(', ') },
          )
        : undefined,
    });
  };

  const downloadTemplate = () => {
    const url = URL.createObjectURL(
      new Blob([bulkTemplateCsv(animalType)], { type: 'text/csv' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `bulk-add-${animalType.toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            {t('animalManagement.bulkAdd.animalsTitle', 'Animals')}
          </h3>
          <p className="text-muted-foreground text-sm">
            {t(
              'animalManagement.bulkAdd.animalsDescription',
              'One row per animal. The values above apply to every row.',
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={downloadTemplate}
          >
            <DownloadIcon className="mr-1.5 size-4" />
            {t('animalManagement.bulkAdd.template', 'Template')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => fileInput.current?.click()}
          >
            <UploadIcon className="mr-1.5 size-4" />
            {t('animalManagement.bulkAdd.importCsv', 'Import CSV')}
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,.tsv,.txt,text/csv,text/plain"
            className="hidden"
            aria-label={t('animalManagement.bulkAdd.importCsv', 'Import CSV')}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void importFile(file);
            }}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-y-1 text-sm">
          <thead>
            <tr>
              <th className="w-8 text-left font-medium">#</th>
              {columns.map((field) => (
                <th key={field} className="px-1 text-left font-medium">
                  {label(field)}
                  {isAddFieldRequired(animalType, field) ? '*' : ''}
                </th>
              ))}
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <BulkRow
                key={row.key}
                row={row}
                index={index}
                columns={columns}
                label={label}
                disabled={disabled}
                onValueChange={(field, value) => setValue(row, field, value)}
                onReasonChange={(reason) =>
                  updateRow(row.key, { reason, error: null })
                }
                onRemove={() => removeRow(row.key)}
                onOpenExisting={onOpenExisting}
              />
            ))}
          </tbody>
        </table>
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-2"
        disabled={disabled}
        onClick={() => onRowsChange([...rows, emptyBulkRow()])}
      >
        <PlusIcon className="mr-1.5 size-4" />
        {t('animalManagement.bulkAdd.addRow', 'Add row')}
      </Button>
    </div>
  );
}

type BulkRowProps = {
  row: BulkAddRow;
  index: number;
  columns: BulkRowField[];
  label: (field: BulkRowField) => string;
  disabled?: boolean;
  onValueChange: (field: BulkRowField, value: string) => void;
  onReasonChange: (reason: string) => void;
  onRemove: () => void;
  onOpenExisting?: (animal: AnimalRow) => void;
};

function BulkRow({
  row,
  index,
  columns,
  label,
  disabled,
  onValueChange,
  onReasonChange,
  onRemove,
  onOpenExisting,
}: BulkRowProps) {
  const { t } = useTranslation();
  const flagged = Boolean(row.duplicates || row.error);

  return (
    <>
      <tr className={flagged ? 'bg-amber-50' : undefined}>
        <td className="text-muted-foreground pr-1">{index + 1}</td>
        {columns.map((field) => (
          <td key={field} className="px-1">
            <Input
              value={row.values[field]}
              disabled={disabled}
              aria-label={`${label(field)} ${index + 1}`}
              placeholder={field === 'birthDate' ? 'YYYY-MM-DD' : undefined}
              onChange={(event) => onValueChange(field, event.target.value)}
            />
          </td>
        ))}
        <td className="pl-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label={t('animalManagement.bulkAdd.removeRow', 'Remove row')}
            onClick={onRemove}
          >
            <Trash2Icon className="size-4" />
          </Button>
        </td>
      </tr>
      {row.error && (
        <tr>
          <td />
          <td
            colSpan={columns.length + 1}
            className="px-1 pb-2 text-sm text-red-700"
            role="alert"
          >
            {row.error}
          </td>
        </tr>
      )}
      {row.duplicates && (
        <tr>
          <td />
          <td colSpan={columns.length + 1} className="px-1 pb-3">
            <PossibleDuplicatesPanel
              duplicates={row.duplicates}
              reason={row.reason}
              onReasonChange={onReasonChange}
              onOpenExisting={onOpenExisting}
              reasonId={`${row.key}-duplicate-reason`}
            />
          </td>
        </tr>
      )}
    </>
  );
}
